import {useStep, type UseStepActions} from '@hconnect/common/hooks'
import React, {createContext, useCallback, useContext, useMemo, useState} from 'react'
import {type FieldValues} from 'react-hook-form'
import {type AnyObjectSchema} from 'yup'

import {useChecklistLocalStorage} from './useChecklistLocalStorage'
import {useNavigationBlocker} from './useNavigationBlocker'
import {useNavigationConfirmation} from './useNavigationConfirmation'

export enum StepperLayoutType {
  Horizontal = 'horizontal',
  SideMenu = 'side-menu'
}

export type StepsConfig<TStepsValues extends FieldValues = FieldValues> = {
  key: keyof TStepsValues
  label: string
  StepFormContent: React.ElementType
  validationSchema?: AnyObjectSchema
  defaultValues?: TStepsValues[keyof TStepsValues]
}[]

export type ChecklistConfig<TStepsValues extends FieldValues = FieldValues> = {
  layout: StepperLayoutType
  useStepsConfig: () => StepsConfig<TStepsValues>
  showSubmitInHeader: boolean
  title: string
}

type UseSteppedFormReturn<TStepsValues extends FieldValues = FieldValues> = {
  activeStep: number
  stepsConfig: StepsConfig
  layout: StepperLayoutType
  stepsValues: TStepsValues
  stepActions: UseStepActions
  updateCurrentStepValues: (values: Partial<TStepsValues[keyof TStepsValues]>) => void
  updateCurrentStepValuesAndSubmit: (values: Partial<TStepsValues[keyof TStepsValues]>) => void
  setCurrentStepDirty: (isDirty: boolean) => void
  onCancel?: () => void
  isSubmitting?: boolean
  readOnly?: boolean
  backToTargetLabel: string
  checklistConfig: ChecklistConfig
  hasUnsavedChanges: boolean
  showExitModal: boolean
  handleCancelExit: () => void
  handleConfirmExit: (skipSave?: boolean) => void
  handleDiscardExit: () => void
}

const SteppedFormContext = createContext<UseSteppedFormReturn | null>(null)

export const useSteppedFormContext = <T extends FieldValues>() => {
  const context = useContext(SteppedFormContext)
  if (!context) {
    throw new Error('useSteppedFormContext must be used within a SteppedFormProvider')
  }
  return context as UseSteppedFormReturn<T>
}

type SteppedFormProviderProps<TFieldValues> = {
  children: React.ReactNode
  initialValues: TFieldValues
  checklistConfig: ChecklistConfig
  readOnly?: boolean
  onCancel?: () => void
  onSubmit?: (values: TFieldValues) => void
  isSubmitting?: boolean
  backToTargetLabel: string
  checklistId: string
}

export const SteppedFormProvider = <T extends FieldValues>({
  children,
  initialValues = {} as T,
  checklistConfig,
  readOnly = false,
  onCancel,
  onSubmit,
  isSubmitting,
  backToTargetLabel,
  checklistId
}: SteppedFormProviderProps<T>) => {
  const {layout, useStepsConfig} = checklistConfig
  const stepsConfig = useStepsConfig()
  const {saveDraft, loadDraft, clearDraft} = useChecklistLocalStorage<T>(checklistId)

  const [stepsValues, setStepsValues] = useState<T>(() => {
    if (readOnly) return initialValues

    const draft = loadDraft()
    return draft ?? initialValues
  })

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isCurrentStepDirty, setIsCurrentStepDirty] = useState(false)
  const [activeStep, stepActions] = useStep(stepsConfig.length)
  const stepsValuesRef = React.useRef(stepsValues)

  stepsValuesRef.current = stepsValues

  const {
    isConfirmationOpen,
    requestConfirmation,
    handleConfirm,
    handleCancel: cancelConfirmation
  } = useNavigationConfirmation()

  const activeStepConfig = stepsConfig[activeStep - 1]

  if (!activeStepConfig) {
    throw new Error(`Invalid step index: ${activeStep}. Valid range: 1-${stepsConfig.length}`)
  }

  const activeStepKey = activeStepConfig.key

  const {isBlocked, proceedNavigation, cancelNavigation} = useNavigationBlocker(
    hasUnsavedChanges || isCurrentStepDirty,
    !readOnly,
    () => saveDraft(stepsValuesRef.current)
  )

  const updateStepValues = useCallback(
    (step: keyof T, data: Partial<T[keyof T]>) => {
      if (readOnly) return

      setStepsValues((prevData) => {
        const newData = {...prevData, [step]: data} as T
        stepsValuesRef.current = newData
        saveDraft(newData)
        return newData
      })
      setHasUnsavedChanges(true)
    },
    [readOnly, saveDraft]
  )

  const updateCurrentStepValues = useCallback(
    (data: Partial<T[keyof T]>) => {
      updateStepValues(activeStepKey, data)
    },
    [activeStepKey, updateStepValues]
  )

  const updateCurrentStepValuesAndSubmit = useCallback(
    (data: Partial<T[keyof T]>) => {
      updateCurrentStepValues(data)
      const updatedStepsValues = {...stepsValues, [activeStepKey]: data}
      onSubmit?.(updatedStepsValues)
      clearDraft()
      setHasUnsavedChanges(false)
      setIsCurrentStepDirty(false)
    },
    [activeStepKey, updateCurrentStepValues, stepsValues, onSubmit, clearDraft]
  )

  const setCurrentStepDirty = useCallback((isDirty: boolean) => {
    setIsCurrentStepDirty(isDirty)
  }, [])

  const handleCancelClick = useCallback(() => {
    if ((hasUnsavedChanges || isCurrentStepDirty) && !readOnly) {
      requestConfirmation(() => {
        if (hasUnsavedChanges) {
          saveDraft(stepsValues)
        }
        void onCancel?.()
      })
    } else {
      void onCancel?.()
    }
  }, [
    hasUnsavedChanges,
    isCurrentStepDirty,
    readOnly,
    requestConfirmation,
    saveDraft,
    stepsValues,
    onCancel
  ])

  const showExitModal = isConfirmationOpen || isBlocked

  const handleCancelExit = useCallback(() => {
    if (isBlocked) {
      cancelNavigation()
    } else {
      cancelConfirmation()
    }
  }, [isBlocked, cancelNavigation, cancelConfirmation])

  const handleConfirmExit = useCallback(
    (skipSave?: boolean) => {
      if (isBlocked) {
        proceedNavigation(skipSave)
      } else {
        handleConfirm()
      }
    },
    [isBlocked, proceedNavigation, handleConfirm]
  )

  const handleDiscardExit = useCallback(() => {
    if (isBlocked) {
      proceedNavigation(true)
    } else {
      handleConfirm()
    }
  }, [isBlocked, proceedNavigation, handleConfirm])

  const value = useMemo<UseSteppedFormReturn<T>>(
    () => ({
      activeStep,
      stepsConfig,
      layout,
      stepsValues,
      stepActions,
      readOnly,
      updateCurrentStepValues,
      onCancel: handleCancelClick,
      updateCurrentStepValuesAndSubmit,
      setCurrentStepDirty,
      isSubmitting,
      backToTargetLabel,
      checklistConfig,
      hasUnsavedChanges,
      showExitModal,
      handleCancelExit,
      handleConfirmExit,
      handleDiscardExit
    }),
    [
      activeStep,
      stepsConfig,
      layout,
      stepsValues,
      stepActions,
      readOnly,
      updateCurrentStepValues,
      handleCancelClick,
      updateCurrentStepValuesAndSubmit,
      setCurrentStepDirty,
      isSubmitting,
      backToTargetLabel,
      checklistConfig,
      hasUnsavedChanges,
      showExitModal,
      handleCancelExit,
      handleConfirmExit,
      handleDiscardExit
    ]
  )

  return <SteppedFormContext.Provider value={value}>{children}</SteppedFormContext.Provider>
}
